import {
    collection,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    addDoc,
    deleteDoc,
    query,
    orderBy,
    where,
    collectionGroup,
    limit,
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';
import { fetchPSOEmail } from '../../../../api/emailService';
import { setDoc } from 'firebase/firestore';

export type Request = {
    id: string;
    requestId?: string;
    requestType?: string;
    createdBy?: string;
    branchCode?: string;
    branchName?: string;
    company?: string;
    companyId?: string;
    requesterName?: string;
    requesterContact?: string;
    requiredBy?: string;
    cctvDateFrom?: any;
    cctvDateTo?: any;
    cctvDateRanges?: { dateFrom: string; dateTo: string; timeFrom: string; timeTo: string }[];
    guardDateFrom?: any;
    guardDateTo?: any;
    bamsDateFrom?: any;
    bamsDateTo?: any;
    timeFrom?: string;
    timeTo?: string;
    numberOfGuards?: number;
    hardwareName?: string;
    purpose?: string;
    purposeOther?: string;
    status?: string;
    createdAt?: any;
    updatedAt?: any;
    forwardedAt?: any;
    forwardedBy?: string;
    updatedBy?: string;
    guardCompany?: string;
    branchId?: string;
    bankId?: string;
    vendorId?: string;
    guardDailyRate?: number;
    totalAmount?: number;
    approvedAt?: any;
    rejectedAt?: any;
    completedAt?: any;
    [key: string]: any;
};

export type CompanyInfo = {
    companyId: string;
    /** Business / logical id (often same as Firestore pos doc id). */
    psoId: string;
    /** Firestore document id under banks/{companyId}/pos/{posDocId} — use for managedPsos subpath. */
    posDocId: string;
    psoDesignation: string;
    psoName: string;
    psoEmail: string;
};

export type Branch = {
    id: string;
    companyId: string;
    branchCode?: string;
    branchName?: string;
    psoId?: string;
    [key: string]: any;
};

/** Firestore stores designations with underscores (see addStaffService). */
export function normalizePsoDesignationLabel(designation: string | null | undefined): string {
    return (designation || '').replace(/_/g, ' ').trim().toLowerCase();
}

/** Regional PSO and Regional Head share the same managed-PSO request workflow. */
export function isRegionalLeadDesignation(designation: string | null | undefined): boolean {
    const n = normalizePsoDesignationLabel(designation);
    return n === 'regional pso' || n === 'regional head';
}

export function isHeadPsoDesignation(designation: string | null | undefined): boolean {
    const n = normalizePsoDesignationLabel(designation);
    return n === 'head pso' || n === 'head';
}

export function isBasePsoDesignation(designation: string | null | undefined): boolean {
    return normalizePsoDesignationLabel(designation) === 'pso';
}

/**
 * Fetch company ID and PSO information for the current user.
 * Tries pos/{authUid} first; if missing, finds pos by field authUid (legacy docs whose id is not the auth uid).
 */
export async function fetchCompanyIdAndPsoInfo(uid: string): Promise<CompanyInfo | null> {
    try {
        const banksSnap = await getDocs(collection(db, 'banks'));
        for (const bankDoc of banksSnap.docs) {
            const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
            const posDoc = await getDoc(posDocRef);
            if (posDoc.exists()) {
                const posData = posDoc.data();
                const posDocId = uid;
                const psoEmailFromDB = await fetchPSOEmail(db, bankDoc.id, posDocId);

                return {
                    companyId: bankDoc.id,
                    psoId: (posData.psoId as string | undefined) || posDocId,
                    posDocId,
                    psoDesignation: (posData.designation as string) || 'PSO',
                    psoName: (posData.name as string) || '',
                    psoEmail: psoEmailFromDB || '',
                };
            }
        }

        try {
            const cg = await getDocs(
                query(collectionGroup(db, 'pos'), where('authUid', '==', uid), limit(1))
            );
            if (!cg.empty) {
                const posSnap = cg.docs[0];
                const pathParts = posSnap.ref.path.split('/');
                const companyId = pathParts[1];
                const posDocId = pathParts[3];
                if (!companyId || !posDocId) return null;
                const posData = posSnap.data();
                const psoEmailFromDB = await fetchPSOEmail(db, companyId, posDocId);
                return {
                    companyId,
                    psoId: (posData.psoId as string | undefined) || posDocId,
                    posDocId,
                    psoDesignation: (posData.designation as string) || 'PSO',
                    psoName: (posData.name as string) || '',
                    psoEmail: psoEmailFromDB || '',
                };
            }
        } catch (cgErr) {
            console.warn('fetchCompanyIdAndPsoInfo: collectionGroup pos lookup failed', cgErr);
        }

        for (const bankDoc of banksSnap.docs) {
            try {
                const innerQ = query(collection(db, 'banks', bankDoc.id, 'pos'), where('authUid', '==', uid), limit(1));
                const inner = await getDocs(innerQ);
                if (!inner.empty) {
                    const posSnap = inner.docs[0];
                    const posDocId = posSnap.id;
                    const posData = posSnap.data();
                    const psoEmailFromDB = await fetchPSOEmail(db, bankDoc.id, posDocId);
                    return {
                        companyId: bankDoc.id,
                        psoId: (posData.psoId as string | undefined) || posDocId,
                        posDocId,
                        psoDesignation: (posData.designation as string) || 'PSO',
                        psoName: (posData.name as string) || '',
                        psoEmail: psoEmailFromDB || '',
                    };
                }
            } catch {
                /* try next bank */
            }
        }

        return null;
    } catch (error) {
        console.error('Error fetching company ID and PSO info:', error);
        return null;
    }
}

/**
 * Fetch branches for a company, filtered by PSO ID
 */
export async function fetchBranches(companyId: string, psoId: string): Promise<Branch[]> {
    try {
        const branchSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
        const branchList = branchSnap.docs.map(doc => ({
            id: doc.id,
            companyId,
            ...doc.data(),
        }));

        // Filter branches by PSO ID
        const filteredBranches = branchList.filter((branch: any) => {
            return branch.psoId === psoId;
        });

        return filteredBranches as Branch[];
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
}

/**
 * Fetch managed PSOs for a Regional PSO or Head PSO.
 * @param posDocId Firestore pos document id (`banks/.../pos/{posDocId}/managedPsos`); equals auth uid when the doc id is the uid.
 */
export async function fetchManagedPsos(
    companyId: string,
    psoId: string,
    psoDesignation: string,
    posDocId: string
): Promise<string[]> {
    if (!isRegionalLeadDesignation(psoDesignation) && !isHeadPsoDesignation(psoDesignation)) {
        return [];
    }

    try {
        // managedPsos.{psoId} is the child pos document id; requests.createdBy may use that id or the child's `psoId` field
        const managedSnap = await getDocs(collection(db, 'banks', companyId, 'pos', posDocId, 'managedPsos'));
        const directManagedIds = managedSnap.docs.map(d => d.data().psoId).filter(Boolean) as string[];

        const expanded = new Set<string>(directManagedIds);
        for (const managedPosDocId of directManagedIds) {
            try {
                const child = await getDoc(doc(db, 'banks', companyId, 'pos', managedPosDocId));
                if (child.exists()) {
                    const d = child.data() as { psoId?: string; authUid?: string };
                    if (d.psoId?.trim()) expanded.add(d.psoId.trim());
                    if (d.authUid?.trim()) expanded.add(d.authUid.trim());
                }
            } catch {
                /* ignore single doc errors */
            }
        }

        let allManagedIds: string[] = Array.from(expanded);

        // If this is a Head PSO, also fetch PSOs managed by Regional PSOs
        if (isHeadPsoDesignation(psoDesignation)) {
            for (const regionalPsoId of directManagedIds) {
                try {
                    const regionalPsoDoc = await getDoc(doc(db, 'banks', companyId, 'pos', regionalPsoId));
                    if (regionalPsoDoc.exists()) {
                        const regionalPosDocId = regionalPsoDoc.id;
                        const regionalManagedSnap = await getDocs(
                            collection(db, 'banks', companyId, 'pos', regionalPosDocId, 'managedPsos')
                        );
                        const regionalManagedIds = regionalManagedSnap.docs.map(doc => doc.data().psoId);
                        allManagedIds = [...allManagedIds, ...regionalManagedIds];
                    }
                } catch (error) {
                    console.error('Error fetching Regional PSO managed PSOs:', error);
                }
            }
        }

        return allManagedIds;
    } catch (error) {
        console.error('Error fetching managed PSOs:', error);
        return [];
    }
}

/**
 * Fetch all requests for a company, filtered by PSO designation
 */
export async function fetchRequests(
    companyId: string,
    psoId: string,
    psoDesignation: string | null,
    managedPsoIds: string[]
): Promise<Request[]> {
    try {
        const requestsSnap = await getDocs(
            query(
                collection(db, 'banks', companyId, 'requests'),
                orderBy('createdAt', 'desc')
            )
        );
        const allRequests = requestsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as Request[];

        // Filter to show requests based on PSO designation
        const requestsList = allRequests.filter(request => {
            let matches = false;

            // Check if user is an incharge
            const isIncharge = psoDesignation && /incharge/i.test(psoDesignation);

            if (isBasePsoDesignation(psoDesignation)) {
                matches = request.createdBy === psoId;
            } else if (isRegionalLeadDesignation(psoDesignation)) {
                // Regional PSO / Regional Head: requests from managed PSOs only (not their own)
                const isManagedRequest = managedPsoIds.includes(request.createdBy || '');
                matches = isManagedRequest;
            } else if (isHeadPsoDesignation(psoDesignation)) {
                matches = request.status === 'forwarded_to_head';
            } else if (isIncharge) {
                // Incharge users can see all requests (filtering by type will be done in UI)
                matches = true;
            } else {
                // Default: Bank users or users without a specific PSO role see all requests
                matches = true;
            }

            return matches;
        });

        // Sort requests: pending first, then by creation date (newest first)
        requestsList.sort((a, b) => {
            const aIsPending = a.status === 'pending';
            const bIsPending = b.status === 'pending';

            // If one is pending and the other isn't, pending comes first
            if (aIsPending && !bIsPending) return -1;
            if (!aIsPending && bIsPending) return 1;

            // If both have same pending status, sort by creation date (newest first)
            const aDate = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds || 0) * 1000;
            const bDate = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds || 0) * 1000;
            return bDate - aDate;
        });

        return requestsList;
    } catch (error) {
        console.error('Error fetching requests:', error);
        throw error;
    }
}

/**
 * Create a new request
 */
export async function createRequest(
    companyId: string,
    requestData: Partial<Request>
): Promise<string> {
    try {
        let finalRequestData = {
            ...requestData,
            createdAt: new Date(),
        };

        // If it's a Guard request, fetch the current rate and calculate total
        if (requestData.requestType === 'Guard') {
            const currentRate = await fetchGuardRate();
            const numberOfGuards = Number(requestData.numberOfGuards) || 0;

            // Calculate days
            let days = 0;
            if (requestData.guardDateFrom && requestData.guardDateTo) {
                const dateFrom = new Date(requestData.guardDateFrom);
                const dateTo = new Date(requestData.guardDateTo);
                const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
                days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Inclusive of start and end date
            } else if (requestData.cctvDateFrom && requestData.cctvDateTo) {
                // Fallback to cctvDate fields if guardDate fields are not set (as shared in form)
                const dateFrom = new Date(requestData.cctvDateFrom);
                const dateTo = new Date(requestData.cctvDateTo);
                const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
                days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            }

            const totalAmount = numberOfGuards * currentRate * days;

            finalRequestData = {
                ...finalRequestData,
                guardDailyRate: currentRate,
                totalAmount: totalAmount
            };
        }

        const docRef = await addDoc(collection(db, 'banks', companyId, 'requests'), finalRequestData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating request:', error);
        throw error;
    }
}

/**
 * Update request status
 */
export async function updateRequestStatus(
    companyId: string,
    requestId: string,
    status: string,
    uid: string,
    extra?: { rejectedReason?: string; rejectedByName?: string }
): Promise<void> {
    try {
        const updateData: any = {
            status,
            updatedAt: new Date(),
            updatedBy: uid,
        };
        
        if (status === 'approved') {
            updateData.approvedAt = new Date();
        } else if (status === 'rejected') {
            updateData.rejectedAt = new Date();
            if (extra?.rejectedReason) {
                updateData.rejectedReason = extra.rejectedReason;
            }
            if (extra?.rejectedByName) {
                updateData.rejectedByName = extra.rejectedByName;
            }
        } else if (status === 'completed') {
            updateData.completedAt = new Date();
        }

        await updateDoc(doc(db, 'banks', companyId, 'requests', requestId), updateData);
    } catch (error) {
        console.error('Error updating request status:', error);
        throw error;
    }
}

/**
 * Permanently remove a request document from Firestore.
 */
export async function deleteRequest(companyId: string, requestId: string): Promise<void> {
    try {
        await deleteDoc(doc(db, 'banks', companyId, 'requests', requestId));
    } catch (error) {
        console.error('Error deleting request:', error);
        throw error;
    }
}

/**
 * Forward request to Head PSO
 */
export async function forwardRequestToHead(
    companyId: string,
    requestId: string,
    uid: string
): Promise<void> {
    try {
        await updateDoc(doc(db, 'banks', companyId, 'requests', requestId), {
            status: 'forwarded_to_head',
            forwardedAt: new Date(),
            forwardedBy: uid,
            updatedAt: new Date(),
            updatedBy: uid,
        });
    } catch (error) {
        console.error('Error forwarding request to head:', error);
        throw error;
    }
}

/**
 * Resubmit request to Regional Head
 */
export async function resubmitRequest(
    companyId: string,
    requestId: string,
    reason: string,
    uid: string
): Promise<void> {
    try {
        await updateDoc(doc(db, 'banks', companyId, 'requests', requestId), {
            status: 'pending',
            resubmitted: true,
            resubmissionReason: reason,
            resubmittedAt: new Date(),
            updatedAt: new Date(),
            updatedBy: uid,
        });
    } catch (error) {
        console.error('Error resubmitting request:', error);
        throw error;
    }
}

export type Company = {
    id: string;
    companyId?: string;
    companyName?: string;
    companyType?: string;
    [key: string]: any;
};

/**
 * Fetch available companies based on request type
 */
export async function fetchAvailableCompanies(
    companyId: string,
    requestType: string
): Promise<Company[]> {
    try {
        const companiesSnap = await getDocs(collection(db, 'banks', companyId, 'companies'));
        const companiesList: Company[] = [];

        let targetCompanyType = '';
        if (requestType === 'CCTV') {
            targetCompanyType = 'CCTV';
        } else if (requestType === 'Guard') {
            targetCompanyType = 'Guard Company';
        } else if (requestType === 'BAMS') {
            targetCompanyType = 'BAMS';
        }

        for (const companyDoc of companiesSnap.docs) {
            const companyData = companyDoc.data();

            if (companyData.companyType === targetCompanyType) {
                if (companyData.companyId && !companyData.companyName) {
                    const globalCompanyDoc = await getDoc(doc(db, 'companies', companyData.companyId));
                    if (globalCompanyDoc.exists()) {
                        const globalCompanyData = globalCompanyDoc.data();
                        companiesList.push({
                            id: companyDoc.id,
                            companyId: globalCompanyData.companyId,
                            companyName: globalCompanyData.companyName,
                            companyType: companyData.companyType,
                            companyAddress: globalCompanyData.companyAddress,
                            companyNo: globalCompanyData.companyNo,
                            email: globalCompanyData.email,
                            isGlobal: true,
                            ...companyData,
                        });
                    }
                } else {
                    companiesList.push({
                        id: companyDoc.id,
                        companyId: companyData.companyId || companyDoc.id,
                        companyName: companyData.companyName,
                        companyType: companyData.companyType,
                        companyAddress: companyData.companyAddress,
                        companyNo: companyData.companyNo,
                        email: companyData.email,
                        isGlobal: false,
                        ...companyData,
                    } as Company);
                }
            }
        }

        return companiesList;
    } catch (error) {
        console.error('Error fetching available companies:', error);
        throw error;
    }
}

/**
 * Fetch the current daily income rate for guards
 */
export async function fetchGuardRate(): Promise<number> {
    try {
        const rateDoc = await getDoc(doc(db, 'settings', 'guard_rates'));
        if (rateDoc.exists()) {
            return rateDoc.data().currentRate || 0;
        }
        return 0;
    } catch (error) {
        console.error('Error fetching guard rate:', error);
        return 0;
    }
}

/**
 * Update the daily income rate for guards
 */
export async function updateGuardRate(rate: number, uid: string): Promise<void> {
    try {
        await setDoc(doc(db, 'settings', 'guard_rates'), {
            currentRate: rate,
            updatedAt: new Date(),
            updatedBy: uid
        });
    } catch (error) {
        console.error('Error updating guard rate:', error);
        throw error;
    }
}

/** Parsed Firestore / JS dates from request docs (same fields as RequestForm guard date range). */
export function parseRequestDateValue(raw: unknown): Date | null {
    if (raw == null) return null;
    if (raw instanceof Date) {
        return Number.isNaN(raw.getTime()) ? null : raw;
    }
    const d = raw as { toDate?: () => Date; seconds?: number; nanoseconds?: number };
    if (typeof d.toDate === 'function') {
        try {
            const td = d.toDate();
            return Number.isNaN(td.getTime()) ? null : td;
        } catch {
            return null;
        }
    }
    if (typeof d.seconds === 'number') {
        const ms = d.seconds * 1000 + (typeof d.nanoseconds === 'number' ? d.nanoseconds / 1e6 : 0);
        const dd = new Date(ms);
        return Number.isNaN(dd.getTime()) ? null : dd;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        const dd = new Date(raw);
        return Number.isNaN(dd.getTime()) ? null : dd;
    }
    if (typeof raw === 'string' && raw.trim()) {
        const dd = new Date(raw);
        return Number.isNaN(dd.getTime()) ? null : dd;
    }
    return null;
}

/** Prefer guardDateFrom/guardDateTo; fallback to CCTV range as used elsewhere in guard calc. */
export function getGuardAssignmentDateRange(doc: Partial<Request>): { from: Date | null; to: Date | null } {
    const from =
        parseRequestDateValue(doc.guardDateFrom) ??
        parseRequestDateValue(doc.cctvDateFrom);
    const to =
        parseRequestDateValue(doc.guardDateTo) ??
        parseRequestDateValue(doc.cctvDateTo);
    return { from, to };
}

/** Start-of local calendar day (noon-safe for DST when comparing boundaries). */
function startOfLocalDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * True when `reference` falls on a day between assignment from/to (inclusive),
 * matching “active through Date To” for approved temporary guard rows in RequestForm.
 */
export function guardAssignmentCoversLocalDay(
    fromRaw: unknown,
    toRaw: unknown,
    reference: Date = new Date()
): boolean {
    const from = parseRequestDateValue(fromRaw);
    const to = parseRequestDateValue(toRaw);
    if (!from || !to) return false;
    const refStart = startOfLocalDay(reference).getTime();
    const fromStart = startOfLocalDay(from).getTime();
    const toStart = startOfLocalDay(to).getTime();
    return fromStart <= refStart && refStart <= toStart;
}

function isApprovedGuardRequest(data: Record<string, unknown>): boolean {
    const rt = String(data.requestType || '').trim().toLowerCase();
    const st = String(data.status || '').trim().toLowerCase();
    return rt === 'guard' && st === 'approved';
}

function tempGuardCountFromDoc(data: Record<string, unknown>): number {
    const n = Number((data as Request).numberOfGuards);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Aggregated temporary guard headcount for Branches table (single bank-wide query). */
export type TemporaryGuardBranchLookup = {
    byBranchId: Map<string, number>;
    byBranchCodeNorm: Map<string, number>;
};

export function normalizeBranchCodeKeyForGuards(code: string | undefined | null): string {
    return String(code ?? '').trim().toLowerCase();
}

export function lookupTemporaryGuardCount(
    lookup: TemporaryGuardBranchLookup | null | undefined,
    branch: { id: string; branchCode?: string }
): number {
    if (!lookup) return 0;
    const byId = lookup.byBranchId.get(branch.id);
    if (byId != null && byId > 0) return byId;
    const key = normalizeBranchCodeKeyForGuards(branch.branchCode);
    if (!key) return 0;
    return lookup.byBranchCodeNorm.get(key) ?? 0;
}

/**
 * One query: all approved Guard requests for the bank, then aggregate by branch id or (if no id) branch code.
 * Rows must cover `asOf` between Date From and Date To (RequestForm guard dates).
 * Composite index: requests collection — requestType + status (equality).
 */
export async function fetchApprovedTemporaryGuardLookupForBank(
    bankId: string,
    asOf: Date = new Date()
): Promise<TemporaryGuardBranchLookup> {
    const coll = collection(db, 'banks', bankId, 'requests');
    const qy = query(coll, where('requestType', '==', 'Guard'), where('status', '==', 'approved'));
    const snap = await getDocs(qy);

    const byBranchId = new Map<string, number>();
    const byBranchCodeNorm = new Map<string, number>();

    for (const d of snap.docs) {
        const rawObj = d.data() as Record<string, unknown>;
        if (!isApprovedGuardRequest(rawObj)) continue;
        const { from: useFrom, to: useTo } = getGuardAssignmentDateRange(rawObj as Partial<Request>);
        if (!guardAssignmentCoversLocalDay(useFrom ?? null, useTo ?? null, asOf)) continue;
        const n = tempGuardCountFromDoc(rawObj);
        if (n <= 0) continue;

        const bid = typeof rawObj.branchId === 'string' ? rawObj.branchId.trim() : '';
        if (bid) {
            byBranchId.set(bid, (byBranchId.get(bid) ?? 0) + n);
            continue;
        }
        const ck = normalizeBranchCodeKeyForGuards(
            typeof rawObj.branchCode === 'string' ? rawObj.branchCode : ''
        );
        if (!ck) continue;
        byBranchCodeNorm.set(ck, (byBranchCodeNorm.get(ck) ?? 0) + n);
    }

    return { byBranchId, byBranchCodeNorm };
}
